import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaEnvelope, FaUser, FaPaperPlane, FaBook, FaMapMarkerAlt } from 'react-icons/fa';
import { device } from '../../styles/responsive';

const ContactSection = styled(motion.section)`
  height: 100vh;
  width: 100%;
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;
  margin: -5vh 0 0 0;
  
  @media ${device.md} {
    height: auto;
    min-height: 100vh;
    padding: 60px 1rem;
    margin: 30px 0;
  }
  
  @media ${device.sm} {
    padding: 40px 1rem;
    min-height: auto;
    margin: 60px 0;
  }
`;

const ContactContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  padding: 2rem;
  position: relative;
  
  @media ${device.lg} {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  
  @media ${device.md} {
    padding: 1rem;
    gap: 2rem;
  }
  
  @media ${device.sm} {
    padding: 0.5rem;
    gap: 1.5rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4rem;
  padding-right: 2rem;
  
  @media ${device.lg} {
    padding-right: 0;
  }
  
  @media ${device.md} {
    gap: 2rem;
  }
  
  @media ${device.sm} {
    gap: 1.5rem;
  }
`;

const ContactTitle = styled.h2`
  color: #ffffff;
  font-size: 3.5rem;
  font-weight: 500;
  margin: 0;
  position: relative;
  padding-bottom: 1rem;
  line-height: 1.2;
  
  @media ${device.lg} {
    font-size: 3rem;
  }
  
  @media ${device.md} {
    font-size: 2.5rem;
  }
  
  @media ${device.sm} {
    font-size: 2rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: #00ffff;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.4);
    
    @media ${device.sm} {
      width: 60px;
      height: 3px;
    }
  }
`;

const ContactDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.3rem;
  line-height: 1.8;
  margin: 0;
  margin-top: 2rem;
  
  @media ${device.md} {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-top: 1.5rem;
  }
  
  @media ${device.sm} {
    font-size: 1.1rem;
    line-height: 1.5;
    margin-top: 1rem;
  }
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media ${device.md} {
    gap: 1.5rem;
  }
  
  @media ${device.sm} {
    gap: 1rem;
  }
`;

const ContactDetail = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  
  @media ${device.md} {
    font-size: 1.1rem;
    padding: 1.2rem;
    gap: 1.2rem;
  }
  
  @media ${device.sm} {
    font-size: 1rem;
    padding: 1rem;
    gap: 1rem;
    border-radius: 12px;
  }
  
  &:hover {
    border-color: rgba(0, 255, 255, 0.3);
    transform: translateX(10px);
    background: rgba(0, 0, 0, 0.4);
  }
  
  svg {
    color: #00ffff;
    font-size: 1.8rem;
    flex-shrink: 0;
    
    @media ${device.md} {
      font-size: 1.6rem;
    }
    
    @media ${device.sm} {
      font-size: 1.4rem;
    }
  }
`;

const FormContainer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 24px;
  padding: 4rem;
  position: relative;
  overflow: hidden;
  
  @media ${device.lg} {
    padding: 3rem;
  }
  
  @media ${device.md} {
    padding: 2rem;
    border-radius: 20px;
  }
  
  @media ${device.sm} {
    padding: 1.25rem;
    border-radius: 16px;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at top right,
      rgba(0, 255, 255, 0.1),
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }
  
  &:hover {
    border-color: rgba(0, 255, 255, 0.2);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 30px rgba(0, 255, 255, 0.1);
      
    &::before {
      opacity: 1;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media ${device.md} {
    gap: 1.5rem;
  }
  
  @media ${device.sm} {
    gap: 0.8rem;
  }
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 1.6rem 1.6rem 1.6rem 4.5rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 16px;
  color: #ffffff;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  @media ${device.md} {
    padding: 1.4rem 1.4rem 1.4rem 4rem;
    font-size: 1.1rem;
    border-radius: 14px;
  }
  
  @media ${device.sm} {
    padding: 1.2rem 1.2rem 1.2rem 3.5rem;
    font-size: 1rem;
    border-radius: 12px;
  }
  
  &:focus {
    outline: none;
    border-color: #00ffff;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1.6rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 16px;
  color: #ffffff;
  font-size: 1.2rem;
  min-height: 250px;
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;
  
  @media ${device.md} {
    padding: 1.4rem;
    font-size: 1.1rem;
    min-height: 180px;
    border-radius: 14px;
  }
  
  @media ${device.sm} {
    padding: 1.2rem;
    font-size: 1rem;
    min-height: 120px;
    border-radius: 12px;
  }
  
  &:focus {
    outline: none;
    border-color: #00ffff;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.5);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 255, 255, 0.5);
  font-size: 1.8rem;
  
  @media ${device.md} {
    left: 1.4rem;
    font-size: 1.6rem;
  }
  
  @media ${device.sm} {
    left: 1.2rem;
    font-size: 1.4rem;
  }
`;

const SubmitButton = styled(motion.button)`
  background: transparent;
  border: 2px solid #00ffff;
  color: #00ffff;
  padding: 1.6rem 3rem;
  border-radius: 16px;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  position: relative;
  overflow: hidden;
  
  @media ${device.md} {
    padding: 1.4rem 2.5rem;
    font-size: 1.2rem;
    border-radius: 14px;
  }
  
  @media ${device.sm} {
    padding: 1.2rem 2rem;
    font-size: 1.1rem;
    border-radius: 12px;
    gap: 0.8rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 255, 0.1),
      transparent
    );
    transition: 0.5s;
  }
  
  &:hover {
    background: rgba(0, 255, 255, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 255, 255, 0.2);
    
    &::before {
      left: 100%;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    font-size: 1.5rem;
    
    @media ${device.sm} {
      font-size: 1.3rem;
    }
  }
`;

const SuccessMessage = styled(motion.div)`
  position: fixed;
  bottom: .5rem;
  left: 35%;
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.2);
  color: #00ff00;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  text-align: center;
  font-size: 1rem;
  z-index: 1000;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 255, 0, 0.1);
  
  @media ${device.md} {
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 400px;
  }
`;

const ErrorMessage = styled(motion.div)`
  position: fixed;
  bottom: .5rem;
  left: 35%;
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.2);
  color: #ff0000;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  text-align: center;
  font-size: 1rem;
  z-index: 1000;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(255, 0, 0, 0.1);
  
  @media ${device.md} {
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 400px;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xqapdoey', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you for your message! I will get back to you soon.'
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 30
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const detailVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <ContactSection
      ref={ref}
      id="contact"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={containerVariants}
    >
      <ContactContainer>
        <ContactInfo>
          <div>
            <ContactTitle>Get in Touch</ContactTitle>
            <ContactDescription>
              Seeking opportunities in AI, ML, and software engineering. Available for both remote and on-site positions, ready to contribute to innovative projects.
            </ContactDescription>
          </div>
          
          <ContactDetails>
            <ContactDetail
              variants={detailVariants}
              custom={0}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <FaUser />
              <span>Mohammad Qataadah Zakir</span>
            </ContactDetail>
            <ContactDetail
              variants={detailVariants}
              custom={1}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <FaEnvelope />
              <span>mqzakir@outlook.com</span>
            </ContactDetail>
            <ContactDetail
              variants={detailVariants}
              custom={2}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <FaMapMarkerAlt />
              <span>Open to opportunities worldwide</span>
            </ContactDetail>
          </ContactDetails>
        </ContactInfo>
        
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <InputIcon>
                <FaUser />
              </InputIcon>
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <InputIcon>
                <FaEnvelope />
              </InputIcon>
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <InputIcon>
                <FaBook />
              </InputIcon>
              <Input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </InputGroup>
            
            <TextArea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
            />
            
            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaPaperPlane />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </SubmitButton>
          </Form>
        </FormContainer>
      </ContactContainer>
      
      <AnimatePresence>
        {status.type === 'success' && (
          <SuccessMessage
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
          >
            {status.message}
          </SuccessMessage>
        )}
        
        {status.type === 'error' && (
          <ErrorMessage
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
          >
            {status.message}
          </ErrorMessage>
        )}
      </AnimatePresence>
    </ContactSection>
  );
};

export default Contact; 